import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Alert, Card, CardContent, CircularProgress } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/store';
import { forgotPassword } from '@/store/slices/authSlice';

const schema = yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

type ForgotPasswordForm = yup.InferType<typeof schema>;

const ForgotPassword: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const { control, handleSubmit } = useForm<ForgotPasswordForm>({
    resolver: yupResolver<ForgotPasswordForm>(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setLoading(true);
    try {
      const res = await dispatch(forgotPassword(data.email)).unwrap();
      setSuccessMsg(res?.message || 'If an account with this email exists, a reset link was sent.');
    } catch (err: any) {
      setErrorMsg(err || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 480 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              Reset your password
            </Typography>

            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : null}>
                {loading ? 'Sending...' : 'Send reset email'}
              </Button>

              <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={() => navigate('/login')}>
                Back to login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
